import {
  type ConsentRequirement,
  type FirebaseIdentity,
  getConsentRequirement,
  httpClient,
  mapClientError,
  readOrganizationGenerationReturn,
  useAuth,
} from "@dutyon/apis";
import {
  AppleIcon,
  Box,
  Button,
  Container,
  GoogleIcon,
  Paper,
  PasswordInput,
  TermsConsentModal,
  type TermsConsentValue,
  Text,
  TextInput,
  Title,
} from "@dutyon/ui-kit";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useLatestLegalTerms } from "../../hooks/useLatestLegalTerms";

interface PendingLogin {
  provider: "google" | "apple" | "email";
  identity: FirebaseIdentity;
  requiredConsent: ConsentRequirement;
}

const publicAppUrl = (import.meta.env.APP_URL || "").replace(/\/+$/, "");

export function LoginPage() {
  const navigate = useNavigate();
  const returnTarget = readOrganizationGenerationReturn(useLocation().search);
  const { setToken, signInWithGoogle, signInWithApple, signInWithEmail, loading, token, logout } =
    useAuth();
  const [pendingLogin, setPendingLogin] = useState<PendingLogin | null>(null);
  const { consentTerms } = useLatestLegalTerms(Boolean(pendingLogin));

  useEffect(() => {
    if (token) {
      navigate(returnTarget ?? "/home");
    }
  }, [token, navigate, returnTarget]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const { email, password } = data;
      const user = await signInWithEmail({ email, password });
      await handleBackendLogin("email", user);
    } catch {
      setError("root", {
        message: "로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.",
      });
    }
  };

  const handleBackendLogin = async (
    provider: "google" | "apple" | "email",
    user: FirebaseIdentity,
    consent?: TermsConsentValue,
  ) => {
    try {
      const response = await httpClient.post<BackofficeLoginResponse>("/auth/social/login", {
        provider,
        token: user.idToken,
        email: user.email,
        name: user.displayName,
        ...consent,
      });

      const { access_token, refresh_token, user: responseUser } = response.data;

      const isAdmin = responseUser.isAdmin;

      if (!isAdmin) {
        setError("root", {
          message: "관리자 권한이 없습니다.",
        });
        return;
      }

      setToken(access_token, refresh_token);
      navigate(returnTarget ?? "/home");
    } catch (error: unknown) {
      const requiredConsent = getConsentRequirement(error);
      if (requiredConsent) {
        setPendingLogin({ provider, identity: user, requiredConsent });
        return;
      }
      setError("root", {
        message: mapClientError(error, {
          fallback: "로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        }).message,
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const user = await signInWithGoogle();
      await handleBackendLogin("google", user);
    } catch (error: unknown) {
      setError("root", {
        message: mapClientError(error, {
          fallback: "Google 로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        }).message,
      });
    }
  };

  const handleAppleLogin = async () => {
    try {
      const user = await signInWithApple();
      await handleBackendLogin("apple", user);
    } catch (error: unknown) {
      setError("root", {
        message: mapClientError(error, {
          fallback: "Apple 로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        }).message,
      });
    }
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f8f9fa", // Gray-50
      }}
    >
      <Container size={420} p={0}>
        <Title ta="center" order={1} mb="md" style={{ color: "#111827" }}>
          DutyOn Backoffice
        </Title>
        <Text c="dimmed" size="sm" ta="center" mb={30}>
          관리자 계정으로 로그인하세요
        </Text>

        <Paper
          withBorder
          shadow="xl"
          py={30}
          px={52}
          miw={420}
          radius="md"
          style={{ backgroundColor: "white" }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextInput
              label="Email"
              placeholder="admin@dutyon.com"
              required
              {...register("email", { required: "이메일을 입력해주세요" })}
              error={errors.email?.message}
            />
            <PasswordInput
              label="Password"
              placeholder="Your password"
              required
              mt="md"
              {...register("password", { required: "비밀번호를 입력해주세요" })}
              error={errors.password?.message}
            />
            {errors.root && (
              <div style={{ color: "#e03131", marginTop: 10, fontSize: 14 }}>
                {errors.root.message}
              </div>
            )}
            <Button fullWidth mt="xl" size="md" type="submit" loading={isSubmitting}>
              로그인
            </Button>

            <Button
              fullWidth
              mt="sm"
              size="md"
              variant="subtle"
              onClick={() => navigate("/signup")}
            >
              이메일로 회원가입
            </Button>

            <div style={{ marginTop: 20, textAlign: "center", color: "#868e96", fontSize: 14 }}>
              또는 소셜 계정으로 로그인
            </div>

            <Button
              fullWidth
              mt="sm"
              size="md"
              variant="default"
              loading={loading}
              onClick={handleGoogleLogin}
              leftSection={<GoogleIcon />}
            >
              Google 계정으로 로그인
            </Button>
            <Button
              fullWidth
              mt="sm"
              size="md"
              variant="default"
              loading={loading}
              onClick={handleAppleLogin}
              leftSection={<AppleIcon />} // Assuming AppleIcon is imported
            >
              Apple 계정으로 로그인
            </Button>
          </form>
        </Paper>
        <TermsConsentModal
          opened={Boolean(pendingLogin)}
          loading={loading}
          terms={
            pendingLogin
              ? {
                  service: consentTerms.service ?? {
                    title: "DutyOn 서비스 이용약관",
                    version: pendingLogin.requiredConsent.serviceTermsVersion,
                  },
                  marketing: consentTerms.marketing ?? {
                    title: "마케팅 정보 수신 동의",
                    version: pendingLogin.requiredConsent.marketingTermsVersion,
                  },
                }
              : undefined
          }
          serviceTermsHref={`${publicAppUrl}/terms/service`}
          marketingTermsHref={`${publicAppUrl}/terms/marketing-info`}
          onCancel={() => {
            setPendingLogin(null);
            void logout();
          }}
          onSubmit={(consent) => {
            if (!pendingLogin) return;
            void handleBackendLogin(pendingLogin.provider, pendingLogin.identity, consent).catch(
              (error: unknown) =>
                setError("root", {
                  message: mapClientError(error, {
                    fallback: "약관 동의 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.",
                  }).message,
                }),
            );
          }}
        />
      </Container>
    </Box>
  );
}

interface LoginFormValues {
  email: string;
  password: string;
}

interface BackofficeLoginResponse {
  access_token: string;
  refresh_token: string;
  user: {
    isAdmin: boolean;
  };
}
